// src/pages/blog/Blog.js
import {
  AlertCircle,
  ArrowRight,
  Bookmark,
  Calendar,
  ChevronRight,
  Clock,
  Eye,
  RefreshCw,
  Search,
  Tag as TagIcon,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useConfig } from "../../context/ConfigContext";
import { getImageUrl } from "../../services/axiosConfig.ts";
import axios from "../../utils/axiosHandler";

const Blog = () => {
  const { config } = useConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [popularPosts, setPopularPosts] = useState([]);

  // State for subscription form
  const [subscriptionData, setSubscriptionData] = useState({
    firstName: "",
    email: "",
    interests: "",
    consent: false,
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState("idle"); // 'idle', 'loading', 'success', 'error'
  const [subscriptionError, setSubscriptionError] = useState("");

  useEffect(() => {
    try {
      // Set the document title using the site name from config
      document.title = "Blog | " + config?.siteName;
    } catch (err) {
      console.error("Error fetching page:", err);
    }
  }, [config?.siteName]); // Add config.siteName to the dependency array

  // Handle subscription form input changes
  const handleSubscriptionChange = (e) => {
    const { name, value } = e.target;
    setSubscriptionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle subscription form submission
  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!subscriptionData.email) {
      setSubscriptionError("Email address is required");
      setSubscriptionStatus("error");
      return;
    }

    if (!subscriptionData.consent) {
      setSubscriptionError("You must agree to receive marketing emails");
      setSubscriptionStatus("error");
      return;
    }

    setSubscriptionStatus("loading");

    try {
      const response = await axios.post("/api/subscriptions", {
        email: subscriptionData.email,
        firstName: subscriptionData.firstName || "",
        interests: subscriptionData.interests || "all",
        phone: subscriptionData.phone || "",
        source: "website_newsletter",
      });

      if (response.status === 400) {
        const errorData = await response.data;
        throw new Error(errorData.error || "Failed to subscribe");
      }

      setSubscriptionStatus("success");
      setSubscriptionData({
        firstName: "",
        email: "",
        interests: "",
        phone: "",
        consent: false,
      });
    } catch (error) {
      console.error("Subscription error:", error);
      setSubscriptionError(
        error.message || "Failed to subscribe. Please try again."
      );
      setSubscriptionStatus("error");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchPosts(currentPage, searchTerm);
  }, [currentPage, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch categories and posts in parallel
      const [categoriesRes, postsRes] = await Promise.all([
        axios.get("/api/blog/categories"),
        axios.get("/api/blog/posts?limit=6&sort=newest"),
      ]);

      if (categoriesRes.status === 404) {
        throw new Error("Failed to fetch categories");
      }
      if (postsRes.status === 404) {
        throw new Error("Failed to fetch posts");
      }

      const categoriesData = await categoriesRes.data;
      const postsData = await postsRes.data;

      setCategories(categoriesData);
      setPosts(postsData.posts);
      setTotalPages(postsData.totalPages);

      // Set featured post (most viewed or first)
      if (postsData.posts.length > 0) {
        const mostViewed = [...postsData.posts].sort(
          (a, b) => b.viewCount - a.viewCount
        )[0];
        setFeaturedPost(mostViewed || postsData.posts[0]);

        // Set popular posts (top 3 by view count)
        const sorted = [...postsData.posts].sort(
          (a, b) => b.viewCount - a.viewCount
        );
        setPopularPosts(sorted.slice(0, 3));
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching blog data:", err);
      setError("Failed to load blog content. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (page, search = "") => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page,
        limit: 6,
        sort: "newest",
      });

      if (search) params.append("search", search);

      const res = await axios.get(`/api/blog/posts?${params.toString()}`);

      if (res.status === 404) {
        throw new Error("Failed to fetch posts");
      }

      const data = await res.data;
      setPosts(data.posts);
      setTotalPages(data.totalPages);

      setError(null);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError("Failed to load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPosts(1, searchTerm);
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate read time in minutes
  const getReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  // Loading skeleton for blog post
  const PostSkeleton = () => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse h-full">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-6">
        <div className="flex gap-2 mb-3">
          <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
          <div className="h-6 w-32 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-7 bg-gray-200 rounded mb-4 w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="flex justify-between items-center">
          <div className="h-5 w-24 bg-gray-200 rounded"></div>
          <div className="h-5 w-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  if (loading && !posts.length && !categories.length) {
    return (
      <div className="min-h-screen bg-gray-50 ">
        {/* Hero section skeleton */}
        <div
          style={{
            background: `linear-gradient(to right, ${config.colors.primary || "#3B82F6"
              }, ${config.colors.accent || "#4F46E5"})`,
            color: "white",
          }}
          className="py-16"
        >
          <div className="container mx-auto px-4">
            <div className="h-10 bg-white bg-opacity-20 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-6 bg-white bg-opacity-20 rounded w-1/2 mx-auto mb-8"></div>
            <div className="max-w-xl mx-auto">
              <div className="h-12 bg-white bg-opacity-20 rounded"></div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PostSkeleton />
              <PostSkeleton />
              <PostSkeleton />
              <PostSkeleton />
            </div>

            <div className="md:w-1/3 space-y-8">
              <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                <div className="h-7 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-2">
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                <div className="h-7 bg-gray-200 rounded mb-4"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !posts.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto text-center">
          <AlertCircle
            className="h-16 w-16 mx-auto mb-4"
            style={{ color: "#EF4444" }}
          />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-white"
            style={{ backgroundColor: config.colors.primary }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = config.colors.primary;
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 mb-0 pb-0">
      <Helmet>
        <title>{"Blog | " + config.siteName}</title>
        <meta
          name="description"
          content="Explore the latest articles on packaging, design trends, and sustainability at Bag&Box."
        />
      </Helmet>

      {/* Hero Section with Animated Background */}
      <div
        className="relative overflow-hidden text-white"
        style={{
          background: `linear-gradient(to right, ${config.colors.primary || "#3B82F6"
            }, ${config.colors.accent || "#4F46E5"})`,
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div
            className="absolute -top-20 -left-20 w-64 h-64 rounded-full animate-float-slow"
            style={{ background: `${config.colors.secondary}` }}
          ></div>
          <div
            className="absolute top-40 right-20 w-80 h-80 rounded-full animate-float"
            style={{ background: `${config.colors.primary}` }}
          ></div>
          <div
            className="absolute bottom-10 left-1/4 w-40 h-40 rounded-full animate-float-fast"
            style={{ background: `${config.colors.accent}` }}
          ></div>
        </div>

        <div className="container mx-auto px-4 py-40 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in">
              {config.siteName}{" "}
              <span
              // style={{ color: `${config.colors.textPrimary}` }}
              >
                Blog
              </span>{" "}
            </h1>
            <p
              className="text-xl max-w-xl mx-auto mb-10 opacity-90 animate-fade-in-delay"
              style={{ color: `white` }}
            >
              {config.siteDescription}
            </p>

            {/* Search Bar with Animation */}
            <div className="mt-8 max-w-xl mx-auto animate-rise">
              <form onSubmit={handleSearch} className="relative group">
                <input
                  type="text"
                  placeholder="Search articles..."
                  className="w-full text-gray-800 px-5 py-4 pr-12 border-0 rounded-full shadow-xl focus:outline-none transition-all duration-300 group-hover:shadow-blue-300/20"
                  style={{
                    ":focus": {
                      outline: `2px solid ${config.colors.primary}60`,
                    },
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                  {searchTerm && (
                    <button
                      style={{
                        boxShadow: "none",
                        border: "none",
                        transform: "none",
                      }}
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        fetchPosts(1);
                      }}
                      className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                      aria-label="Clear search"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                  <button
                    type="submit"
                    className="text-white p-2 rounded-full transition-all duration-300 focus:outline-none"
                    style={{
                      backgroundColor: config.colors.primary,
                      ":focus": {
                        outline: `2px solid ${config.colors.primary}80`,
                      },
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor =
                        config.colors.primary;
                    }}
                    aria-label="Search"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{ display: "block", marginBottom: "-1px" }}
        >
          <svg
            style={{ display: "block", marginBottom: "-1px" }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 120"
            className="w-full h-auto fill-current text-gray-50"
          >
            <path d="M0,64L60,64C120,64,240,64,360,74.7C480,85,600,107,720,101.3C840,96,960,64,1080,53.3C1200,43,1320,53,1380,58.7L1440,64L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-md animate-fade-in">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="flex items-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 relative">
                {searchTerm
                  ? `Search Results for "${searchTerm}"`
                  : "Latest Articles"}
                <span
                  className="absolute -bottom-2 left-0 w-20 h-1 rounded-full"
                  style={{ backgroundColor: config.colors.primary }}
                ></span>
              </h2>
              <div className="ml-8 h-px bg-gray-200 flex-grow"></div>
            </div>

            {loading && !posts.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <PostSkeleton key={i} />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-8 text-center animate-fade-in">
                <div className="flex justify-center mb-6">
                  <AlertCircle className="h-16 w-16 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-3">
                  No posts found
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchTerm
                    ? `We couldn't find any articles matching "${searchTerm}".`
                    : "No blog posts are currently available."}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      fetchPosts(1);
                    }}
                    className="font-medium transition-colors"
                    style={{ color: config.colors.primary }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = `${config.colors.primary}DD`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = config.colors.primary;
                    }}
                  >
                    Clear search and show all posts
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {posts.map((post, index) => (
                  <div
                    key={post._id}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex flex-col h-full">
                      <div className="block overflow-hidden">
                        <Link to={`/blog/${post.slug}`}>
                          <div className="relative h-52 overflow-hidden">
                            {(post.coverImage || post.featuredImage) ? (
                              <img
                                src={getImageUrl(
                                  post.coverImage ||
                                  (typeof post.featuredImage === 'string' ? post.featuredImage : post.featuredImage?.url) ||
                                  ''
                                )}
                                alt={post.title}
                                className="h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                              />
                            ) : (
                              <div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                <Bookmark className="h-10 w-10 text-gray-300" />
                              </div>
                            )}
                            {/* Category Tag */}
                            <div className="absolute top-4 left-4">
                              <span
                                className="text-white text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm"
                                style={{
                                  backgroundColor: `${config.colors.primary}E6`,
                                }}
                              >
                                {post.category?.name || "General"}
                              </span>
                            </div>
                          </div>

                          <div className="p-6 flex-grow flex flex-col">
                            <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Calendar
                                  className="h-4 w-4 mr-1"
                                  style={{
                                    color: `${config.colors.primary}AA`,
                                  }}
                                />
                                {formatDate(post.publishedAt || post.createdAt)}
                              </div>
                            </div>

                            <h3 className="text-xl font-bold mb-3 text-gray-800 transition-colors line-clamp-2">
                              {post.title}
                            </h3>

                            <p className="text-gray-600 mb-6 line-clamp-3 flex-grow">
                              {post.excerpt}
                            </p>
                            <div className="flex items-center mb-3">
                              {featuredPost && (
                                <Link
                                  to={`/blog/${featuredPost.slug}`}
                                  className="inline-flex items-center px-4 py-2 text-white rounded-full text-sm font-medium transition-all duration-300 transform group-hover:translate-x-1"
                                  style={{
                                    backgroundColor: config.colors.primary,
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      config.colors.primary;
                                  }}
                                >
                                  Read Post
                                  <ArrowRight className="h-4 w-4 ml-1" />
                                </Link>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                                  <User className="h-4 w-4" />
                                </div>
                                <span className="ml-2 text-sm text-gray-700">
                                  {
                                    // post.author?.firstName ||
                                    config.siteName + " Team"
                                  }
                                </span>
                              </div>
                              <div className="flex items-center">
                                <Clock
                                  className="h-4 w-4 mr-1"
                                  style={{
                                    color: `${config.colors.primary}AA`,
                                  }}
                                />
                                {post.readTime || getReadTime(post.content)} min
                              </div>
                              <div className="flex items-center text-sm text-gray-500">
                                <Eye className="h-4 w-4 mr-1" />
                                {post.viewCount}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination with Modern Design */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center animate-fade-in">
                <div className="inline-flex items-center bg-white rounded-lg shadow-md p-1">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-md flex items-center ${currentPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-50"
                      }`}
                    style={{
                      ":hover": {
                        color: currentPage !== 1 ? config.colors.primary : "",
                      },
                    }}
                  >
                    <ChevronRight className="h-4 w-4 mr-1 rotate-180" />
                    Previous
                  </button>

                  <div className="hidden md:flex">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 mx-1 rounded-md flex items-center justify-center transition-colors ${currentPage === page
                            ? "text-white"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                          style={{
                            backgroundColor:
                              currentPage === page ? config.colors.primary : "",
                            ":hover": {
                              color:
                                currentPage !== page
                                  ? config.colors.primary
                                  : "",
                            },
                          }}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <div className="md:hidden flex items-center mx-2">
                    <span className="px-3 py-1 text-gray-600 font-medium">
                      {currentPage} / {totalPages}
                    </span>
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-md flex items-center ${currentPage === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-50"
                      }`}
                    style={{
                      ":hover": {
                        color:
                          currentPage !== totalPages
                            ? config.colors.primary
                            : "",
                      },
                    }}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}

            {/* Featured Post Section with Motion Effects */}
            {featuredPost && !searchTerm && (
              <div className="mb-12 mt-16 animate-fade-in">
                <div className="flex items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 relative">
                    Featured Post
                    <span
                      className="absolute -bottom-2 left-0 w-20 h-1 rounded-full"
                      style={{ backgroundColor: config.colors.primary }}
                    ></span>
                  </h2>
                  <div className="ml-8 h-px bg-gray-200 flex-grow"></div>
                </div>

                <div className="group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
                  <div className="md:flex h-full">
                    <div className="md:w-1/2 relative overflow-hidden">
                      {(featuredPost.coverImage || featuredPost.featuredImage) ? (
                        <img
                          src={getImageUrl(
                            featuredPost.coverImage ||
                            (typeof featuredPost.featuredImage === 'string' ? featuredPost.featuredImage : featuredPost.featuredImage?.url) ||
                            ''
                          )}
                          alt={featuredPost.title}
                          className="h-64 md:h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="h-64 md:h-full w-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                          <Bookmark className="h-16 w-16 text-blue-200" />
                        </div>
                      )}
                      <div className="absolute top-4 left-4 animate-pulse-slow">
                        <span
                          className="text-white text-xs uppercase font-semibold px-3 py-1 rounded-full shadow-md"
                          style={{ backgroundColor: config.colors.primary }}
                        >
                          Featured
                        </span>
                      </div>
                    </div>

                    <div className="md:w-1/2 p-8 flex flex-col">
                      <div className="flex items-center mb-4 text-sm">
                        <span
                          className="px-3 py-1 rounded-full font-medium"
                          style={{
                            backgroundColor: `${config.colors.primary}20`,
                            color: `${config.colors.primary}E6`,
                          }}
                        >
                          {featuredPost.category?.name || "General"}
                        </span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-gray-500 flex items-center">
                          <Calendar
                            className="h-4 w-4 mr-1"
                            style={{ color: `${config.colors.primary}AA` }}
                          />
                          {formatDate(
                            featuredPost.publishedAt || featuredPost.createdAt
                          )}
                        </span>
                      </div>

                      <h3 className="text-2xl font-bold mb-4 text-gray-800 transition-colors">
                        <Link
                          to={`/blog/${featuredPost.slug}`}
                          style={{
                            ":hover": { color: config.colors.primary },
                          }}
                        >
                          {featuredPost.title}
                        </Link>
                      </h3>

                      <p className="text-gray-600 mb-6 flex-grow line-clamp-3">
                        {featuredPost.excerpt}
                      </p>

                      <div className="flex items-center justify-between mt-auto border-t border-gray-100 pt-4">
                        <div className="flex items-center space-x-6">
                          <span className="text-gray-500 text-sm flex items-center">
                            <Clock
                              className="h-4 w-4 mr-1"
                              style={{ color: `${config.colors.primary}AA` }}
                            />
                            {featuredPost.readTime ||
                              getReadTime(featuredPost.content)}{" "}
                            min read
                          </span>
                          <span className="text-gray-500 text-sm flex items-center">
                            <Eye
                              className="h-4 w-4 mr-1"
                              style={{ color: `${config.colors.primary}AA` }}
                            />
                            {featuredPost.viewCount} views
                          </span>
                        </div>

                        <Link
                          to={`/blog/${featuredPost.slug}`}
                          className="inline-flex items-center px-4 py-2 text-white rounded-full text-sm font-medium transition-all duration-300 transform group-hover:translate-x-1"
                          style={{ backgroundColor: config.colors.primary }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                              config.colors.primary;
                          }}
                        >
                          Read Post
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar with Modern Design */}
          <div className="lg:w-1/4 space-y-8">
            {/* Categories */}
            <div className="bg-white rounded-xl shadow-md p-6 animate-fade-in-delay">
              <h3 className="text-xl font-bold text-gray-800 mb-6 relative">
                Categories
                <span
                  className="absolute -bottom-2 left-0 w-12 h-1 rounded-full"
                  style={{ backgroundColor: config.colors.primary }}
                ></span>
              </h3>
              <div className="space-y-1">
                {categories.map((category) => (
                  <Link
                    key={category._id}
                    to={`/blog/category/${category.slug}`}
                    className="flex items-center justify-between py-3 px-4 text-gray-700 rounded-lg hover:bg-blue-50 transition-colors group"
                    style={{
                      ":hover": {
                        color: config.colors.primary,
                        backgroundColor: `${config.colors.primary}10`,
                      },
                    }}
                  >
                    <span className="flex items-center">
                      <TagIcon
                        className="h-4 w-4 mr-2"
                        style={{ color: config.colors.primary }}
                      />
                      {category.name}
                    </span>
                    <ChevronRight
                      className="h-4 w-4 text-gray-400 transform transition-transform group-hover:translate-x-1"
                      style={{
                        ":group-hover": { color: config.colors.primary },
                      }}
                    />
                  </Link>
                ))}
              </div>
            </div>

            {/* Newsletter with Glass Morphism */}
            <div className="relative overflow-hidden rounded-xl shadow-lg animate-fade-in-delay-2">
              <div
                className="absolute inset-0 z-0"
                style={{
                  background: `linear-gradient(to bottom right, ${config.colors.primary
                    }, ${config.colors.accent || "#4F46E5"})`,
                }}
              ></div>
              <div
                className="absolute inset-0 opacity-20 z-0"
                style={{ backgroundColor: config.colors.primary }}
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white opacity-10 animate-float-slow"></div>
                <div className="absolute bottom-10 -left-10 w-40 h-40 rounded-full bg-white opacity-10 animate-float"></div>
              </div>

              <div className="relative z-10 backdrop-blur-sm bg-white/10 p-6 text-white">
                {subscriptionStatus === "success" ? (
                  <div className="text-center py-8 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 text-white mb-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                    <p className="text-blue-100">
                      You're now on our list to receive the latest news and
                      updates.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-4">
                    <h3 className="text-xl font-bold mb-1">Stay Updated</h3>
                    <p className="text-blue-100 text-sm mb-4">
                      Get packaging insights delivered to your inbox
                    </p>

                    <div>
                      <label
                        htmlFor="subscribeFirstName"
                        className="block text-sm mb-1 text-blue-100"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        id="subscribeFirstName"
                        name="firstName"
                        value={subscriptionData.firstName}
                        onChange={handleSubscriptionChange}
                        className="text-gray-800 w-full px-4 py-2 border-0 rounded-lg focus:ring-2 bg-white/90 backdrop-blur-sm"
                        style={{
                          ":focus": {
                            outlineColor: `${config.colors.primary}80`,
                          },
                        }}
                        placeholder="Your first name"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subscribeEmail"
                        className="block text-sm mb-1 text-blue-100"
                      >
                        Email Address*
                      </label>
                      <input
                        type="email"
                        id="subscribeEmail"
                        name="email"
                        value={subscriptionData.email}
                        onChange={handleSubscriptionChange}
                        required
                        className="text-gray-800 w-full px-4 py-2 border-0 rounded-lg focus:ring-2 bg-white/90 backdrop-blur-sm"
                        style={{
                          ":focus": {
                            outlineColor: `${config.colors.primary}80`,
                          },
                        }}
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="subscribeConsent"
                          name="consent"
                          type="checkbox"
                          checked={subscriptionData.consent}
                          onChange={(e) =>
                            setSubscriptionData({
                              ...subscriptionData,
                              consent: e.target.checked,
                            })
                          }
                          required
                          className="h-4 w-4 border-blue-300 rounded"
                          style={{
                            accentColor: config.colors.primary,
                          }}
                        />
                      </div>
                      <div className="ml-3">
                        <label
                          htmlFor="subscribeConsent"
                          className="text-sm text-blue-100"
                        >
                          I agree to receive marketing emails. You can
                          unsubscribe anytime.
                        </label>
                      </div>
                    </div>

                    {subscriptionStatus === "error" && (
                      <div className="text-red-200 text-sm p-2 bg-red-500/20 rounded-lg backdrop-blur-sm">
                        {subscriptionError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={subscriptionStatus === "loading"}
                      className="w-full py-3 bg-white font-semibold rounded-lg transition-colors duration-300 flex items-center justify-center group"
                      style={{
                        color: config.colors.primary,
                        ":hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      {subscriptionStatus === "loading" ? (
                        <div className="flex items-center">
                          <svg
                            className="animate-spin mr-2 h-4 w-4"
                            style={{ color: config.colors.primary }}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                          Processing...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          Subscribe Now
                          <ArrowRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Popular Posts with Motion Effects */}
            {popularPosts.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 animate-fade-in-delay-3">
                <h3 className="text-xl font-bold text-gray-800 mb-6 relative">
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      style={{ color: config.colors.primary }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    Popular Posts
                  </div>
                  <span
                    className="absolute -bottom-2 left-0 w-12 h-1 rounded-full"
                    style={{ backgroundColor: config.colors.primary }}
                  ></span>
                </h3>
                <div className="space-y-4">
                  {popularPosts.map((post, index) => (
                    <Link
                      key={post._id}
                      to={`/blog/${post.slug}`}
                      className="flex gap-3 group p-2 rounded-lg hover:bg-gray-50 transition-colors"
                      style={{
                        animationDelay: `${index * 100}ms`,
                        ":hover": {
                          backgroundColor: `${config.colors.primary}10`,
                        },
                      }}
                    >
                      <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden">
                        {(post.coverImage || post.featuredImage?.url) ? (
                          <img
                            src={getImageUrl(post.coverImage || post.featuredImage?.url)}
                            alt={post.featuredImage?.alt || post.title}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <Bookmark className="h-6 w-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h4
                          className="font-medium text-gray-800 transition-colors line-clamp-2"
                          style={{
                            ":group-hover": { color: config.colors.primary },
                          }}
                        >
                          {post.title}
                        </h4>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <Calendar
                            className="h-3 w-3 mr-1"
                            style={{ color: `${config.colors.primary}AA` }}
                          />
                          {formatDate(post.publishedAt || post.createdAt)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* About the Blog with Card */}
            <div className="bg-white rounded-xl shadow-md p-6 animate-fade-in-delay-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4 relative">
                About Our Blog
                <span
                  className="absolute -bottom-2 left-0 w-12 h-1 rounded-full"
                  style={{ backgroundColor: config.colors.primary }}
                ></span>
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Welcome to the Bag&Box blog! Here we share packaging trends,
                sustainability practices, design tips, and industry insights to
                help you stay informed and inspired.
              </p>
              <Link
                to="/contactUs"
                className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-medium transition-all duration-300 group"
                style={{ backgroundColor: config.colors.primary }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = config.colors.primary;
                }}
              >
                Contact Us
                <ArrowRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Social Media Links with Modern Style */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6 animate-fade-in-delay-5">
              <h3 className="text-xl font-bold text-gray-800 mb-6 relative">
                Follow Us on Instagram
                <span
                  className="absolute -bottom-2 left-0 w-12 h-1 rounded-full"
                  style={{ backgroundColor: config.colors.primary }}
                ></span>
              </h3>
              <div className="flex space-x-3 justify-center">


                <a
                  href={config.socialLinks?.instagram || 'https://www.instagram.com/limadrivingschool'}
                  target="_blank"
                  className="p-3 rounded-full text-white transition-transform hover:scale-110 shadow-md"
                  style={{
                    background:
                      "linear-gradient(to bottom right, #833AB4, #FD1D1D, #FCAF45)",
                  }}
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Banner */}
      {!searchTerm && (
        <div
          className="py-16 mt-12 relative overflow-hidden animate-fade-in-delay-5"
          style={{
            background: `linear-gradient(to right, ${config.colors.primary}, ${config.colors.accent || "#4F46E5"
              })`,
          }}
        >
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute top-0 left-1/4 w-64 h-64 rounded-full opacity-20"
              style={{ background: `${config.colors.accent}` }}
            ></div>
            <div
              className="absolute -bottom-20 right-1/4 w-80 h-80 rounded-full opacity-20"
              style={{ background: `${config.colors.accent}` }}
            ></div>
            <div
              className="absolute top-1/2 right-1/3 w-40 h-40 rounded-full opacity-20"
              style={{ background: `${config.colors.accent}` }}
            ></div>
          </div>

          <div className="container mx-auto px-4 text-center relative z-10 mb-0">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to book your driving lesson?
            </h2>
            <p className="text-white max-w-2xl mx-auto mb-8">
              Book your driving lesson today and start your journey to becoming a safe and confident driver.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/book/driving-lesson"
                className="px-6 py-3 bg-white font-semibold rounded-full transition-colors duration-300 shadow-lg"
                style={{
                  color: config.colors.primary,
                  ":hover": {
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                Book Your Driving Lesson
              </Link>
              <Link
                to="/contactUs"
                className="px-6 py-3 bg-opacity-30 backdrop-blur-sm text-white border border-opacity-30 font-semibold rounded-full transition-colors duration-300 shadow-lg"
                style={{
                  backgroundColor: `${config.colors.primary}4D`, // 30% opacity
                  borderColor: `${config.colors.primary}4D`,
                  ":hover": {
                    backgroundColor: `${config.colors.primary}66`, // 40% opacity
                  },
                }}
              >
                Contact Our Team
              </Link>
            </div>
          </div>
        </div>
      )}

      <style>{`
        body {
          overflow-x: hidden;
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes float-slow {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        
        @keyframes float-fast {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes rise {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes pulse-slow {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-float-fast {
          animation: float-fast 4s ease-in-out infinite;
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .animate-fade-in-delay {
          animation: fade-in 0.8s ease-out 0.3s forwards;
          opacity: 0;
        }
        
        .animate-fade-in-delay-2 {
          animation: fade-in 0.8s ease-out 0.6s forwards;
          opacity: 0;
        }
        
        .animate-fade-in-delay-3 {
          animation: fade-in 0.8s ease-out 0.9s forwards;
          opacity: 0;
        }
        
        .animate-fade-in-delay-4 {
          animation: fade-in 0.8s ease-out 1.2s forwards;
          opacity: 0;
        }
        
        .animate-fade-in-delay-5 {
          animation: fade-in 0.8s ease-out 1.5s forwards;
          opacity: 0;
        }
        
        .animate-rise {
          animation: rise 1s ease-out forwards;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Blog;

// // befor dynamic color from config
// // src/pages/blog/Blog.js
// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { Search, Calendar, Eye, Tag as TagIcon, ChevronRight, AlertCircle, Clock, User, Bookmark, ArrowRight, RefreshCw } from 'lucide-react';
// import { Helmet } from 'react-helmet';

// const Blog = () => {
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [posts, setPosts] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [featuredPost, setFeaturedPost] = useState(null);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [totalPages, setTotalPages] = useState(1);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [popularPosts, setPopularPosts] = useState([]);

//   // State for subscription form
//   const [subscriptionData, setSubscriptionData] = useState({
//     firstName: '',
//     email: '',
//     interests: '',
//     consent: false
//   });
//   const [subscriptionStatus, setSubscriptionStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
//   const [subscriptionError, setSubscriptionError] = useState('');

//   // Handle subscription form input changes
//   const handleSubscriptionChange = (e) => {
//     const { name, value } = e.target;
//     setSubscriptionData(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   // Handle subscription form submission
//   const handleSubscribe = async (e) => {
//     e.preventDefault();

//     if (!subscriptionData.email) {
//       setSubscriptionError('Email address is required');
//       setSubscriptionStatus('error');
//       return;
//     }

//     if (!subscriptionData.consent) {
//       setSubscriptionError('You must agree to receive marketing emails');
//       setSubscriptionStatus('error');
//       return;
//     }

//     setSubscriptionStatus('loading');

//     try {
//       const response = await fetch('/api/subscriptions', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json'
//         },
//         body: JSON.stringify({
//           email: subscriptionData.email,
//           firstName: subscriptionData.firstName || '',
//           interests: subscriptionData.interests || 'all',
//           phone: subscriptionData.phone || '',
//           source: 'website_newsletter'
//         })
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.error || 'Failed to subscribe');
//       }

//       setSubscriptionStatus('success');
//       setSubscriptionData({
//         firstName: '',
//         email: '',
//         interests: '',
//         phone: '',
//         consent: false
//       });

//     } catch (error) {
//       console.error('Subscription error:', error);
//       setSubscriptionError(error.message || 'Failed to subscribe. Please try again.');
//       setSubscriptionStatus('error');
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   useEffect(() => {
//     fetchPosts(currentPage, searchTerm);
//   }, [currentPage, searchTerm]);

//   const fetchData = async () => {
//     setLoading(true);
//     try {
//       // Fetch categories and posts in parallel
//       const [categoriesRes, postsRes] = await Promise.all([
//         fetch('/api/blog/categories'),
//         fetch('/api/blog/posts?limit=6&sort=newest')
//       ]);

//       if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
//       if (!postsRes.ok) throw new Error('Failed to fetch posts');

//       const categoriesData = await categoriesRes.json();
//       const postsData = await postsRes.json();

//       setCategories(categoriesData);
//       setPosts(postsData.posts);
//       setTotalPages(postsData.totalPages);

//       // Set featured post (most viewed or first)
//       if (postsData.posts.length > 0) {
//         const mostViewed = [...postsData.posts].sort((a, b) => b.viewCount - a.viewCount)[0];
//         setFeaturedPost(mostViewed || postsData.posts[0]);

//         // Set popular posts (top 3 by view count)
//         const sorted = [...postsData.posts].sort((a, b) => b.viewCount - a.viewCount);
//         setPopularPosts(sorted.slice(0, 3));
//       }

//       setError(null);
//     } catch (err) {
//       console.error('Error fetching blog data:', err);
//       setError('Failed to load blog content. Please try again later.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchPosts = async (page, search = '') => {
//     setLoading(true);
//     try {
//       // Build query params
//       const params = new URLSearchParams({
//         page,
//         limit: 6,
//         sort: 'newest'
//       });

//       if (search) params.append('search', search);

//       const res = await fetch(`/api/blog/posts?${params.toString()}`);

//       if (!res.ok) throw new Error('Failed to fetch posts');

//       const data = await res.json();
//       setPosts(data.posts);
//       setTotalPages(data.totalPages);

//       setError(null);
//     } catch (err) {
//       console.error('Error fetching posts:', err);
//       setError('Failed to load posts. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSearch = (e) => {
//     e.preventDefault();
//     setCurrentPage(1);
//     fetchPosts(1, searchTerm);
//   };

//   // Format date
//   const formatDate = (dateString) => {
//     const options = { year: 'numeric', month: 'long', day: 'numeric' };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };

//   // Calculate read time in minutes
//   const getReadTime = (content) => {
//     const wordsPerMinute = 200;
//     const wordCount = content.split(/\s+/).length;
//     return Math.ceil(wordCount / wordsPerMinute);
//   };

//   // Loading skeleton for blog post
//   const PostSkeleton = () => (
//     <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse h-full">
//       <div className="h-48 bg-gray-200"></div>
//       <div className="p-6">
//         <div className="flex gap-2 mb-3">
//           <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
//           <div className="h-6 w-32 bg-gray-200 rounded-full"></div>
//         </div>
//         <div className="h-7 bg-gray-200 rounded mb-4 w-3/4"></div>
//         <div className="h-4 bg-gray-200 rounded mb-2"></div>
//         <div className="h-4 bg-gray-200 rounded mb-2"></div>
//         <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
//         <div className="flex justify-between items-center">
//           <div className="h-5 w-24 bg-gray-200 rounded"></div>
//           <div className="h-5 w-20 bg-gray-200 rounded"></div>
//         </div>
//       </div>
//     </div>
//   );

//   if (loading && !posts.length && !categories.length) {
//     return (
//       <div className="min-h-screen bg-gray-50 ">
//         {/* Hero section skeleton */}
//         <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16 ">
//           <div className="container mx-auto px-4">
//             <div className="h-10 bg-white bg-opacity-20 rounded w-1/3 mx-auto mb-4"></div>
//             <div className="h-6 bg-white bg-opacity-20 rounded w-1/2 mx-auto mb-8"></div>
//             <div className="max-w-xl mx-auto">
//               <div className="h-12 bg-white bg-opacity-20 rounded"></div>
//             </div>
//           </div>
//         </div>

//         <div className="container mx-auto px-4 py-12">
//           <div className="flex flex-col md:flex-row gap-8">
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             <PostSkeleton />
//             <PostSkeleton />
//             <PostSkeleton />
//             <PostSkeleton />
//           </div>

//             <div className="md:w-1/3 space-y-8">
//               <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
//                 <div className="h-7 bg-gray-200 rounded mb-4"></div>
//                 <div className="space-y-2">
//                   <div className="h-6 bg-gray-200 rounded"></div>
//                   <div className="h-6 bg-gray-200 rounded"></div>
//                   <div className="h-6 bg-gray-200 rounded"></div>
//                 </div>
//               </div>

//               <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
//                 <div className="h-7 bg-gray-200 rounded mb-4"></div>
//                 <div className="h-24 bg-gray-200 rounded"></div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (error && !posts.length) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto text-center">
//           <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
//           <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <button
//             onClick={() => window.location.reload()}
//             className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors inline-flex items-center"
//           >
//             <RefreshCw className="h-4 w-4 mr-2" />
//             Try Again
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 mb-0 pb-0">
//       <Helmet>
//         <title>Blog | BagBox</title>
//         <meta name="description" content="Explore the latest articles on packaging, design trends, and sustainability at Bag&Box." />
//       </Helmet>

//       {/* Hero Section with Animated Background */}
//       <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 to-indigo-900 text-white">
//         {/* Animated Background Elements */}
//         <div className="absolute inset-0 overflow-hidden opacity-20">
//           <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-blue-400 animate-float-slow"></div>
//           <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-indigo-500 animate-float"></div>
//           <div className="absolute bottom-10 left-1/4 w-40 h-40 rounded-full bg-blue-300 animate-float-fast"></div>
//         </div>

//         <div className="container mx-auto px-4 py-40 relative z-10">
//           <div className="text-center max-w-3xl mx-auto">
//             <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in">
//               Bag&Box <span className="text-blue-100">Blog</span>
//             </h1>
//             <p className="text-xl text-blue-100 max-w-xl mx-auto mb-10 opacity-90 animate-fade-in-delay">
//               Insights into packaging, design trends, and sustainability
//             </p>

//             {/* Search Bar with Animation */}
//             <div className="mt-8 max-w-xl mx-auto animate-rise">
//               <form onSubmit={handleSearch} className="relative group">
//                 <input
//                   type="text"
//                   placeholder="Search articles..."
//                   className="w-full text-gray-800 px-5 py-4 pr-12 border-0 rounded-full shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300 group-hover:shadow-blue-300/20"
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//                 <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
//                   {searchTerm && (
//                     <button
//                     style={{
//                       boxShadow: 'none',
//                       border: 'none',
//                       transform: 'none',
//                     }}
//                       type="button"
//                       onClick={() => {
//                         setSearchTerm('');
//                         fetchPosts(1);
//                       }}
//                       className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
//                       aria-label="Clear search"
//                     >
//                       <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                     </button>
//                   )}
//                   <button
//                     type="submit"
//                     className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
//                     aria-label="Search"
//                   >
//                     <Search className="h-5 w-5" />
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>

//         {/* Wave Divider */}
//         <div className="absolute bottom-0 left-0 right-0"
//         style={{ display: 'block', marginBottom: '-1px' }}>
//           <svg style={{ display: 'block', marginBottom: '-1px' }}
//           xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto fill-current text-gray-50">
//             <path d="M0,64L60,64C120,64,240,64,360,74.7C480,85,600,107,720,101.3C840,96,960,64,1080,53.3C1200,43,1320,53,1380,58.7L1440,64L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"></path>
//           </svg>
//         </div>
//       </div>

//       <div className="container mx-auto px-4 py-12">
//         {error && (
//           <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-md animate-fade-in">
//             <div className="flex items-center">
//               <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
//               <span className="text-red-700">{error}</span>
//             </div>
//           </div>
//         )}

//         <div className="flex flex-col lg:flex-row gap-8">
//           {/* Main Content */}
//           <div className="lg:w-3/4">
//             <div className="flex items-center mb-8">
//               <h2 className="text-2xl font-bold text-gray-800 relative">
//                 {searchTerm ? `Search Results for "${searchTerm}"` : 'Latest Articles'}
//                 <span className="absolute -bottom-2 left-0 w-20 h-1 bg-blue-600 rounded-full"></span>
//               </h2>
//               <div className="ml-8 h-px bg-gray-200 flex-grow"></div>
//             </div>

//             {loading && !posts.length ? (
//               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//                 {[...Array(6)].map((_, i) => (
//                   <PostSkeleton key={i} />
//                 ))}
//               </div>
//             ) : posts.length === 0 ? (
//               <div className="bg-white rounded-xl shadow-md p-8 text-center animate-fade-in">
//                 <div className="flex justify-center mb-6">
//                   <AlertCircle className="h-16 w-16 text-gray-400" />
//                 </div>
//                 <h3 className="text-xl font-semibold text-gray-700 mb-3">No posts found</h3>
//                 <p className="text-gray-500 mb-6">
//                   {searchTerm
//                     ? `We couldn't find any articles matching "${searchTerm}".`
//                     : "No blog posts are currently available."}
//                 </p>
//                 {searchTerm && (
//                   <button
//                     onClick={() => {
//                       setSearchTerm('');
//                       fetchPosts(1);
//                     }}
//                     className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
//                   >
//                     Clear search and show all posts
//                   </button>
//                 )}
//               </div>
//             ) : (
//               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
//                 {posts.map((post, index) => (
//                   <div
//                     key={post._id}
//                     className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 animate-fade-in"
//                     style={{ animationDelay: `${index * 100}ms` }}
//                   >
//                     <div className="flex flex-col h-full">
//                       <div className="block overflow-hidden">
//                         <Link to={`/blog/${post.slug}`} >
//                           <div className="relative h-52 overflow-hidden">
//                             {post.coverImage ? (
//                               <img
//                                 src={post.coverImage}
//                                 alt={post.title}
//                                 className="h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-500"
//                               />
//                             ) : (
//                               <div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
//                                 <Bookmark className="h-10 w-10 text-gray-300" />
//                               </div>
//                             )}
//                             {/* Category Tag */}
//                             <div className="absolute top-4 left-4">
//                               <span className="bg-blue-600 bg-opacity-90 text-white text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm">
//                                 {post.category?.name || 'General'}
//                               </span>
//                             </div>
//                           </div>

//                           <div className="p-6 flex-grow flex flex-col">
//                             <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
//                               <div className="flex items-center">
//                               <Calendar className="h-4 w-4 mr-1 text-blue-500" />
//                                 {formatDate(post.publishedAt || post.createdAt)}
//                               </div>
//                             </div>

//                             <h3 className="text-xl font-bold mb-3 text-gray-800 group-hover:text-blue-600 transition-colors line-clamp-2">
//                               {post.title}
//                             </h3>

//                             <p className="text-gray-600 mb-6 line-clamp-3 flex-grow">
//                               {post.excerpt}
//                             </p>
//                             <div className="flex items-center mb-3">
//                               {featuredPost && (

//                                 <Link
//                                   to={`/blog/${featuredPost.slug}`}
//                                   className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium transition-all duration-300 hover:bg-blue-700 transform group-hover:translate-x-1"
//                                 >
//                                   Read Post
//                                   <ArrowRight className="h-4 w-4 ml-1" />
//                                 </Link>
//                               )}
//                             </div>
//                             <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">

//                               <div className="flex items-center">
//                                 <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
//                                   <User className="h-4 w-4" />
//                                 </div>
//                                 <span className="ml-2 text-sm text-gray-700">{
//                                 // post.author?.firstName ||
//                                   'BagBox Team'}</span>
//                               </div>
//                               <div className="flex items-center">
//                                 <Clock className="h-4 w-4 mr-1 text-blue-500" />
//                                 {post.readTime || getReadTime(post.content)} min
//                               </div>
//                               <div className="flex items-center text-sm text-gray-500">
//                                 <Eye className="h-4 w-4 mr-1" />
//                                 {post.viewCount}
//                               </div>
//                             </div>
//                           </div>
//                         </Link>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}

//             {/* Pagination with Modern Design */}
//             {totalPages > 1 && (
//               <div className="mt-12 flex justify-center animate-fade-in">
//                 <div className="inline-flex items-center bg-white rounded-lg shadow-md p-1">
//                   <button
//                     onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//                     disabled={currentPage === 1}
//                     className={`px-4 py-2 rounded-md flex items-center ${
//                       currentPage === 1
//                         ? 'text-gray-400 cursor-not-allowed'
//                         : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
//                     }`}
//                   >
//                     <ChevronRight className="h-4 w-4 mr-1 rotate-180" />
//                     Previous
//                   </button>

//                   <div className="hidden md:flex">
//                     {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//                       <button
//                         key={page}
//                         onClick={() => setCurrentPage(page)}
//                         className={`w-10 h-10 mx-1 rounded-md flex items-center justify-center transition-colors ${
//                           currentPage === page
//                             ? 'bg-blue-600 text-white'
//                             : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
//                         }`}
//                       >
//                         {page}
//                       </button>
//                     ))}
//                   </div>

//                   <div className="md:hidden flex items-center mx-2">
//                     <span className="px-3 py-1 text-gray-600 font-medium">
//                       {currentPage} / {totalPages}
//                     </span>
//                   </div>

//                   <button
//                     onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//                     disabled={currentPage === totalPages}
//                     className={`px-4 py-2 rounded-md flex items-center ${
//                       currentPage === totalPages
//                         ? 'text-gray-400 cursor-not-allowed'
//                         : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
//                     }`}
//                   >
//                     Next
//                     <ChevronRight className="h-4 w-4 ml-1" />
//                   </button>
//                 </div>
//               </div>
//             )}

//             {/* Featured Post Section with Motion Effects */}
//             {featuredPost && !searchTerm && (
//               <div className="mb-12 mt-16 animate-fade-in">
//                 <div className="flex items-center mb-8">
//                   <h2 className="text-2xl font-bold text-gray-800 relative">
//                     Featured Post
//                     <span className="absolute -bottom-2 left-0 w-20 h-1 bg-blue-600 rounded-full"></span>
//                   </h2>
//                   <div className="ml-8 h-px bg-gray-200 flex-grow"></div>
//                 </div>

//                 <div className="group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
//                   <div className="md:flex h-full">
//                     <div className="md:w-1/2 relative overflow-hidden">
//                       {featuredPost.coverImage ? (
//                         <img
//                           src={featuredPost.coverImage}
//                           alt={featuredPost.title}
//                           className="h-64 md:h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-700"
//                         />
//                       ) : (
//                         <div className="h-64 md:h-full w-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
//                           <Bookmark className="h-16 w-16 text-blue-200" />
//                         </div>
//                       )}
//                       <div className="absolute top-4 left-4 animate-pulse-slow">
//                         <span className="bg-blue-600 text-white text-xs uppercase font-semibold px-3 py-1 rounded-full shadow-md">
//                           Featured
//                         </span>
//                       </div>
//                     </div>

//                     <div className="md:w-1/2 p-8 flex flex-col">
//                       <div className="flex items-center mb-4 text-sm">
//                         <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
//                           {featuredPost.category?.name || 'General'}
//                         </span>
//                         <span className="mx-2 text-gray-400">•</span>
//                         <span className="text-gray-500 flex items-center">
//                           <Calendar className="h-4 w-4 mr-1 text-blue-500" />
//                           {formatDate(featuredPost.publishedAt || featuredPost.createdAt)}
//                         </span>
//                       </div>

//                       <h3 className="text-2xl font-bold mb-4 text-gray-800 group-hover:text-blue-600 transition-colors">
//                         <Link to={`/blog/${featuredPost.slug}`}>
//                           {featuredPost.title}
//                         </Link>
//                       </h3>

//                       <p className="text-gray-600 mb-6 flex-grow line-clamp-3">
//                         {featuredPost.excerpt}
//                       </p>

//                       <div className="flex items-center justify-between mt-auto border-t border-gray-100 pt-4">
//                         <div className="flex items-center space-x-6">
//                           <span className="text-gray-500 text-sm flex items-center">
//                             <Clock className="h-4 w-4 mr-1 text-blue-500" />
//                             {featuredPost.readTime || getReadTime(featuredPost.content)} min read
//                           </span>
//                           <span className="text-gray-500 text-sm flex items-center">
//                             <Eye className="h-4 w-4 mr-1 text-blue-500" />
//                             {featuredPost.viewCount} views
//                           </span>
//                         </div>

//                         <Link
//                           to={`/blog/${featuredPost.slug}`}
//                           className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium transition-all duration-300 hover:bg-blue-700 transform group-hover:translate-x-1"
//                         >
//                           Read Post
//                           <ArrowRight className="h-4 w-4 ml-1" />
//                         </Link>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>

//           {/* Sidebar with Modern Design */}
//           <div className="lg:w-1/4 space-y-8">
//             {/* Categories */}
//             <div className="bg-white rounded-xl shadow-md p-6 animate-fade-in-delay">
//               <h3 className="text-xl font-bold text-gray-800 mb-6 relative">
//                 Categories
//                 <span className="absolute -bottom-2 left-0 w-12 h-1 bg-blue-600 rounded-full"></span>
//               </h3>
//               <div className="space-y-1">
//                 {categories.map(category => (
//                   <Link
//                     key={category._id}
//                     to={`/blog/category/${category.slug}`}
//                     className="flex items-center justify-between py-3 px-4 text-gray-700 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors group"
//                   >
//                     <span className="flex items-center">
//                       <TagIcon className="h-4 w-4 mr-2 text-blue-500" />
//                       {category.name}
//                     </span>
//                     <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transform transition-transform group-hover:translate-x-1" />
//                   </Link>
//                 ))}
//               </div>
//             </div>

//             {/* Newsletter with Glass Morphism */}
//             <div className="relative overflow-hidden rounded-xl shadow-lg animate-fade-in-delay-2">
//               <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-800 z-0"></div>
//               <div className="absolute inset-0 bg-blue-500 opacity-20 z-0">
//                 <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white opacity-10 animate-float-slow"></div>
//                 <div className="absolute bottom-10 -left-10 w-40 h-40 rounded-full bg-white opacity-10 animate-float"></div>
//               </div>

//               <div className="relative z-10 backdrop-blur-sm bg-white/10 p-6 text-white">
//                 {subscriptionStatus === 'success' ? (
//                   <div className="text-center py-8 animate-fade-in">
//                     <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 text-white mb-4">
//                       <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                       </svg>
//                     </div>
//                     <h3 className="text-xl font-bold mb-2">Thank You!</h3>
//                     <p className="text-blue-100">You're now on our list to receive the latest news and updates.</p>
//                   </div>
//                 ) : (
//                   <form onSubmit={handleSubscribe} className="space-y-4">
//                     <h3 className="text-xl font-bold mb-1">Stay Updated</h3>
//                     <p className="text-blue-100 text-sm mb-4">Get packaging insights delivered to your inbox</p>

//                     <div>
//                       <label htmlFor="subscribeFirstName" className="block text-sm mb-1 text-blue-100">First Name</label>
//                       <input
//                         type="text"
//                         id="subscribeFirstName"
//                         name="firstName"
//                         value={subscriptionData.firstName}
//                         onChange={handleSubscriptionChange}
//                         className="text-gray-800 w-full px-4 py-2 border-0 rounded-lg focus:ring-2 focus:ring-blue-300 bg-white/90 backdrop-blur-sm"
//                         placeholder="Your first name"
//                       />
//                     </div>

//                     <div>
//                       <label htmlFor="subscribeEmail" className="block text-sm mb-1 text-blue-100">Email Address*</label>
//                       <input
//                         type="email"
//                         id="subscribeEmail"
//                         name="email"
//                         value={subscriptionData.email}
//                         onChange={handleSubscriptionChange}
//                         required
//                         className="text-gray-800 w-full px-4 py-2 border-0 rounded-lg focus:ring-2 focus:ring-blue-300 bg-white/90 backdrop-blur-sm"
//                         placeholder="your@email.com"
//                       />
//                     </div>

//                     <div className="flex items-start">
//                       <div className="flex items-center h-5">
//                         <input
//                           id="subscribeConsent"
//                           name="consent"
//                           type="checkbox"
//                           checked={subscriptionData.consent}
//                           onChange={(e) => setSubscriptionData({...subscriptionData, consent: e.target.checked})}
//                           required
//                           className="h-4 w-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
//                         />
//                       </div>
//                       <div className="ml-3">
//                         <label htmlFor="subscribeConsent" className="text-sm text-blue-100">
//                           I agree to receive marketing emails. You can unsubscribe anytime.
//                         </label>
//                       </div>
//                     </div>

//                     {subscriptionStatus === 'error' && (
//                       <div className="text-red-200 text-sm p-2 bg-red-500/20 rounded-lg backdrop-blur-sm">
//                         {subscriptionError}
//                       </div>
//                     )}

//                     <button
//                       type="submit"
//                       disabled={subscriptionStatus === 'loading'}
//                       className="w-full py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center group"
//                     >
//                       {subscriptionStatus === 'loading' ? (
//                         <div className="flex items-center">
//                           <svg className="animate-spin mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
//                           </svg>
//                           Processing...
//                         </div>
//                       ) : (
//                         <div className="flex items-center">
//                           Subscribe Now
//                           <ArrowRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
//                         </div>
//                       )}
//                     </button>
//                   </form>
//                 )}
//               </div>
//             </div>

//             {/* Popular Posts with Motion Effects */}
//             {popularPosts.length > 0 && (
//               <div className="bg-white rounded-xl shadow-md p-6 animate-fade-in-delay-3">
//                 <h3 className="text-xl font-bold text-gray-800 mb-6 relative">
//                   <div className="flex items-center">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
//                     </svg>
//                     Popular Posts
//                   </div>
//                   <span className="absolute -bottom-2 left-0 w-12 h-1 bg-blue-600 rounded-full"></span>
//                 </h3>
//                 <div className="space-y-4">
//                   {popularPosts.map((post, index) => (
//                     <Link
//                       key={post._id}
//                       to={`/blog/${post.slug}`}
//                       className="flex gap-3 group p-2 rounded-lg hover:bg-gray-50 transition-colors"
//                       style={{ animationDelay: `${index * 100}ms` }}
//                     >
//                       <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden">
//                         {post.coverImage ? (
//                           <img
//                             src={post.coverImage}
//                             alt={post.title}
//                             className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
//                           />
//                         ) : (
//                           <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
//                             <Bookmark className="h-6 w-6 text-gray-300" />
//                           </div>
//                         )}
//                       </div>
//                       <div>
//                         <h4 className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors line-clamp-2">
//                           {post.title}
//                         </h4>
//                         <div className="flex items-center mt-1 text-xs text-gray-500">
//                           <Calendar className="h-3 w-3 mr-1 text-blue-500" />
//                           {formatDate(post.publishedAt || post.createdAt)}
//                         </div>
//                       </div>
//                     </Link>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* About the Blog with Card */}
//             <div className="bg-white rounded-xl shadow-md p-6 animate-fade-in-delay-4">
//               <h3 className="text-xl font-bold text-gray-800 mb-4 relative">
//                 About Our Blog
//                 <span className="absolute -bottom-2 left-0 w-12 h-1 bg-blue-600 rounded-full"></span>
//               </h3>
//               <p className="text-gray-600 mb-4 leading-relaxed">
//                 Welcome to the Bag&Box blog! Here we share packaging trends, sustainability practices, design tips, and industry insights to help you stay informed and inspired.
//               </p>
//               <Link
//                 to="/contactUs"
//                 className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium transition-all duration-300 hover:bg-blue-700 group"
//               >
//                 Contact Us
//                 <ArrowRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
//               </Link>
//             </div>

//             {/* Social Media Links with Modern Style */}
//             <div className="bg-white rounded-xl shadow-md p-6 mb-6 animate-fade-in-delay-5">
//               <h3 className="text-xl font-bold text-gray-800 mb-6 relative">
//                 Follow Us
//                 <span className="absolute -bottom-2 left-0 w-12 h-1 bg-blue-600 rounded-full"></span>
//               </h3>
//               <div className="flex space-x-3 justify-center">
//                 <a href="#" className="bg-blue-600 p-3 rounded-full text-white hover:bg-blue-700 transition-transform hover:scale-110 shadow-md">
//                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
//                   </svg>
//                 </a>
//                 <a href="#" className="bg-blue-400 p-3 rounded-full text-white hover:bg-blue-500 transition-transform hover:scale-110 shadow-md">
//                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
//                   </svg>
//                 </a>
//                 <a href="https://www.instagram.com/bagbox.ca" target="_blank" className="bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 p-3 rounded-full text-white transition-transform hover:scale-110 shadow-md">
//                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
//                   </svg>
//                 </a>
//                 <a href="#" className="bg-indigo-600 p-3 rounded-full text-white hover:bg-indigo-700 transition-transform hover:scale-110 shadow-md">
//                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
//                   </svg>
//                 </a>
//                 <a href="#" className="bg-red-600 p-3 rounded-full text-white hover:bg-red-700 transition-transform hover:scale-110 shadow-md">
//                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"></path>
//                   </svg>
//                 </a>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Newsletter Banner */}
//       {!searchTerm && (
//         <div className="bg-gradient-to-r from-blue-600 to-indigo-800 py-16 mt-12 relative overflow-hidden animate-fade-in-delay-5">
//           {/* Background Elements */}
//           <div className="absolute inset-0 overflow-hidden">
//             <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-blue-400 opacity-20"></div>
//             <div className="absolute -bottom-20 right-1/4 w-80 h-80 rounded-full bg-indigo-500 opacity-20"></div>
//             <div className="absolute top-1/2 right-1/3 w-40 h-40 rounded-full bg-blue-300 opacity-20"></div>
//           </div>

//           <div className="container mx-auto px-4 text-center relative z-10 mb-0">
//             <h2 className="text-3xl font-bold text-white mb-6">Ready to Enhance Your Packaging Experience?</h2>
//             <p className="text-blue-100 max-w-2xl mx-auto mb-8">
//               Stay updated with our latest articles, product releases, and sustainable packaging solutions.
//             </p>
//             <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
//               <Link
//                 to="/blog"
//                 className="px-6 py-3 bg-white text-blue-700 font-semibold rounded-full hover:bg-blue-50 transition-colors duration-300 shadow-lg"
//               >
//                 Explore Articles
//               </Link>
//               <Link
//                 to="/contactUs"
//                 className="px-6 py-3 bg-blue-800 bg-opacity-30 backdrop-blur-sm text-white border border-blue-400 border-opacity-30 font-semibold rounded-full hover:bg-opacity-50 transition-colors duration-300 shadow-lg"
//               >
//                 Contact Our Team
//               </Link>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Back to Top Button */}
//       {/* <button
//         onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
//         className="fixed bottom-6 right-6 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors focus:outline-none"
//         aria-label="Back to top"
//       >
//         <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
//         </svg>
//       </button> */}

//       <style>{`
//         body {
//           overflow-x: hidden;
//         }

//         .wave-divider {
//           display: none; /* Hide any existing wave dividers */
//         }

//         /* Target any blue background elements that might be causing the line */
//         .blue-bg, .bg-blue-600, .bg-blue-700, .bg-blue-800, .bg-[#033568] {
//           position: relative;
//           z-index: 1;
//         }
//         @keyframes float {
//           0% { transform: translateY(0px); }
//           50% { transform: translateY(-15px); }
//           100% { transform: translateY(0px); }
//         }

//         @keyframes float-slow {
//           0% { transform: translateY(0px) rotate(0deg); }
//           50% { transform: translateY(-20px) rotate(5deg); }
//           100% { transform: translateY(0px) rotate(0deg); }
//         }

//         @keyframes float-fast {
//           0% { transform: translateY(0px); }
//           50% { transform: translateY(-10px); }
//           100% { transform: translateY(0px); }
//         }

//         @keyframes fade-in {
//           from { opacity: 0; transform: translateY(10px); }
//           to { opacity: 1; transform: translateY(0); }
//         }

//         @keyframes rise {
//           from { opacity: 0; transform: translateY(20px); }
//           to { opacity: 1; transform: translateY(0); }
//         }

//         @keyframes pulse-slow {
//           0% { transform: scale(1); }
//           50% { transform: scale(1.05); }
//           100% { transform: scale(1); }
//         }

//         .animate-float {
//           animation: float 6s ease-in-out infinite;
//         }

//         .animate-float-slow {
//           animation: float-slow 8s ease-in-out infinite;
//         }

//         .animate-float-fast {
//           animation: float-fast 4s ease-in-out infinite;
//         }

//         .animate-fade-in {
//           animation: fade-in 0.8s ease-out forwards;
//         }

//         .animate-fade-in-delay {
//           animation: fade-in 0.8s ease-out 0.3s forwards;
//           opacity: 0;
//         }

//         .animate-fade-in-delay-2 {
//           animation: fade-in 0.8s ease-out 0.6s forwards;
//           opacity: 0;
//         }

//         .animate-fade-in-delay-3 {
//           animation: fade-in 0.8s ease-out 0.9s forwards;
//           opacity: 0;
//         }

//         .animate-fade-in-delay-4 {
//           animation: fade-in 0.8s ease-out 1.2s forwards;
//           opacity: 0;
//         }

//         .animate-fade-in-delay-5 {
//           animation: fade-in 0.8s ease-out 1.5s forwards;
//           opacity: 0;
//         }

//         .animate-rise {
//           animation: rise 1s ease-out forwards;
//         }

//         .animate-pulse-slow {
//           animation: pulse-slow 3s ease-in-out infinite;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default Blog;
